import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ArrowRight,
  CloudOff,
  FileDown,
  FileText,
  ImagePlus,
  LayoutTemplate,
  type LucideIcon,
  Mic,
  PanelRight,
  Sparkles,
  X,
} from "lucide-react";
import type { JSX } from "react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { cn } from "@/lib/utils";
import type { WhatsNewFeature, WhatsNewFeatureId } from "@/lib/whats-new";

const ICONS: Record<WhatsNewFeatureId, LucideIcon> = {
  workspace: PanelRight,
  upload: ImagePlus,
  export: FileDown,
  offline: CloudOff,
  templates: LayoutTemplate,
  letters: FileText,
  shortcuts: Mic,
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  features: readonly WhatsNewFeature[];
  onTry: (feature: WhatsNewFeature) => void;
}

// v3.2 app (VITE_APP_REDESIGN_ENABLED): v3 tokens; classic otherwise.
const pick = (classic: string, v3: string): string =>
  AppConfig.enableAppRedesign === true ? v3 : classic;

const FeatureCard = ({
  feature,
  index,
  onTry,
}: {
  feature: WhatsNewFeature;
  index: number;
  onTry: (feature: WhatsNewFeature) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const moreId = useId();
  const Icon = ICONS[feature.id];
  const title = t(`whatsNew.features.${feature.id}.title`);

  return (
    <li
      className="animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-both duration-500 motion-reduce:animate-none"
      data-testid={`whats-new-card-${feature.id}`}
      style={{ animationDelay: `${120 + index * 60}ms` }}
    >
      <div
        className={pick(
          "flex gap-3 rounded-2xl border border-tinted-grey-1 bg-grey-white p-3.5 sm:items-center sm:gap-4 sm:p-4",
          "flex gap-3 rounded-[14px] bg-v3-surface-2 p-3.5 shadow-v3-hairline sm:items-center sm:gap-4 sm:p-4",
        )}
      >
        <span
          aria-hidden
          className={pick(
            "flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/15 sm:size-11",
            "flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-v3-panel text-v3-text shadow-v3-hairline",
          )}
        >
          <Icon className={pick("size-[22px]", "size-5")} strokeWidth={1.9} />
        </span>
        <div className="min-w-0 flex-1">
          <h3
            className={pick(
              "text-pretty text-[15px] font-bold leading-snug text-foreground sm:text-base",
              "text-pretty text-[15px] font-semibold leading-snug text-v3-text",
            )}
          >
            {title}
          </h3>
          <p
            className={pick(
              "mt-0.5 text-pretty text-sm leading-snug text-foreground/70",
              "mt-0.5 text-pretty text-[13.5px] leading-snug text-v3-text-2",
            )}
          >
            {t(`whatsNew.features.${feature.id}.body`)}
            {feature.learnMore && (
              <>
                {" "}
                {/* Inline, with a 44px tall hit area around the text. */}
                <button
                  aria-controls={expanded ? moreId : undefined}
                  aria-expanded={expanded}
                  className={cn(
                    "relative whitespace-nowrap rounded-sm font-semibold underline-offset-2 after:absolute after:-inset-x-1.5 after:-inset-y-3.5 after:content-[''] hover:underline",
                    pick(
                      "text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                      "text-v3-text focus-visible:outline-2 focus-visible:outline-v3-brand",
                    ),
                  )}
                  type="button"
                  onClick={() => {
                    setExpanded((value) => !value);
                  }}
                >
                  {expanded ? t("whatsNew.showLess") : t("whatsNew.learnMore")}
                </button>
              </>
            )}
          </p>
          {feature.learnMore && expanded && (
            <p
              className={cn(
                "mt-1.5 leading-snug animate-in fade-in-0 motion-reduce:animate-none",
                pick(
                  "text-sm text-foreground/70",
                  "text-[13.5px] text-v3-text-2",
                ),
              )}
              id={moreId}
            >
              {t(`whatsNew.features.${feature.id}.more` as never)}
            </p>
          )}
          <div className="mt-2 flex sm:hidden">
            <button
              aria-label={t("whatsNew.tryItLabel", { feature: title })}
              className={pick(
                "inline-flex min-h-11 items-center gap-1.5 rounded-full bg-primary/10 px-4 text-sm font-bold text-primary transition-colors hover:bg-primary/15 active:bg-primary/20 dark:bg-primary/20 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:hidden",
                "inline-flex min-h-11 items-center gap-1.5 rounded-full bg-v3-panel px-4 font-v3 text-[13.5px] font-semibold text-v3-text shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-3 focus-visible:outline-2 focus-visible:outline-v3-brand sm:hidden",
              )}
              type="button"
              onClick={() => {
                onTry(feature);
              }}
            >
              {t("whatsNew.tryIt")}
              <ArrowRight aria-hidden className="size-4" />
            </button>
          </div>
        </div>
        <button
          aria-label={t("whatsNew.tryItLabel", { feature: title })}
          className={pick(
            "hidden min-h-10 shrink-0 items-center gap-1.5 self-center rounded-full bg-primary/10 px-4 text-sm font-bold text-primary transition-colors hover:bg-primary hover:text-primary-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:bg-primary/20 sm:inline-flex",
            "hidden h-9 shrink-0 items-center gap-1.5 self-center rounded-full bg-v3-panel px-3.5 font-v3 text-[13.5px] font-semibold text-v3-text shadow-v3-hairline-strong transition-colors hover:bg-v3-ink hover:text-v3-on-ink focus-visible:outline-2 focus-visible:outline-v3-brand sm:inline-flex",
          )}
          type="button"
          onClick={() => {
            onTry(feature);
          }}
        >
          {t("whatsNew.tryIt")}
          <ArrowRight aria-hidden className="size-4" />
        </button>
      </div>
    </li>
  );
};

// "What's new in ROSC": a bottom sheet on phones, a centred dialog from
// 640px. The cards scroll between a fixed header and the "Got it" footer.
export const WhatsNewDialog = ({
  open,
  onOpenChange,
  features,
  onTry,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            pick(
              "bg-black/40 backdrop-blur-[2px] dark:bg-black/60",
              "bg-black/30 dark:bg-black/50",
            ),
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed z-50 flex flex-col overflow-hidden outline-hidden duration-300",
            pick(
              "bg-background text-foreground shadow-2xl",
              "bg-v3-panel font-v3 text-v3-text shadow-v3-pop",
            ),
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            // Phones: bottom sheet.
            "inset-x-0 bottom-0 max-h-[92dvh] data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            pick(
              "rounded-t-[28px] border-t border-tinted-grey-1",
              "rounded-t-[22px]",
            ),
            // From 640px: centred dialog.
            "sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[min(760px,calc(100dvh-48px))] sm:w-[min(680px,calc(100vw-48px))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95",
            pick("sm:rounded-[28px] sm:border", "sm:rounded-[18px]"),
            "motion-reduce:animate-none",
          )}
          data-testid="whats-new-dialog"
          ref={contentRef}
          tabIndex={-1}
          // Focus the dialog itself (read from its title), not the close
          // button, so nothing looks selected when it opens.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            contentRef.current?.focus({ preventScroll: true });
          }}
        >
          <div
            aria-hidden
            className={cn(
              "mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full sm:hidden",
              pick("bg-foreground/15", "bg-v3-border-strong"),
            )}
          />
          <div
            className={pick(
              "relative shrink-0 px-5 pb-4 pt-4 sm:px-8 sm:pb-5 sm:pt-8",
              "relative shrink-0 px-5 pb-4 pt-4 sm:px-6 sm:pb-4 sm:pt-6",
            )}
          >
            {/* Classic: a soft ROSC-blue wash behind the header. */}
            {!AppConfig.enableAppRedesign && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 -top-24 h-48 bg-[radial-gradient(60%_100%_at_30%_0%,hsl(var(--primary)/0.14),transparent)] dark:bg-[radial-gradient(60%_100%_at_30%_0%,hsl(var(--primary)/0.22),transparent)]"
              />
            )}
            <div className="relative flex items-center gap-3.5">
              <span
                aria-hidden
                className={pick(
                  "flex size-11 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[0_6px_16px_-6px_hsl(var(--primary)/0.7)]",
                  "flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-v3-ink text-v3-on-ink",
                )}
              >
                <Sparkles
                  className={pick("size-[22px]", "size-5")}
                  strokeWidth={2}
                />
              </span>
              <div className="min-w-0 pr-10">
                <DialogPrimitive.Title
                  className={pick(
                    "text-[22px] font-extrabold leading-tight tracking-tight sm:text-2xl",
                    "font-v3-display text-[20px] font-semibold leading-tight tracking-tight text-v3-text",
                  )}
                >
                  {t("whatsNew.title")}
                </DialogPrimitive.Title>
                <DialogPrimitive.Description
                  className={pick(
                    "mt-0.5 text-balance text-sm text-foreground/70 sm:text-[15px]",
                    "mt-0.5 text-balance text-[13.5px] leading-snug text-v3-text-2",
                  )}
                >
                  {t("whatsNew.subtitle")}
                </DialogPrimitive.Description>
              </div>
            </div>
            <DialogPrimitive.Close
              aria-label={t("common.close")}
              className={pick(
                "absolute right-2 top-2 flex size-11 items-center justify-center rounded-full text-foreground/60 transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:right-4 sm:top-4",
                "absolute right-2 top-2 flex size-11 items-center justify-center rounded-full text-v3-text-2 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-2 focus-visible:outline-v3-brand sm:right-3 sm:top-3 sm:size-9",
              )}
            >
              <X aria-hidden className="size-5" />
            </DialogPrimitive.Close>
          </div>

          <ul
            className={pick(
              "min-h-0 flex-1 space-y-2.5 overflow-y-auto overscroll-contain px-4 pb-4 sm:space-y-3 sm:px-8",
              "min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain px-4 pb-4 sm:px-6",
            )}
            data-testid="whats-new-cards"
          >
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.id}
                feature={feature}
                index={index}
                onTry={onTry}
              />
            ))}
          </ul>

          <div
            className={pick(
              "shrink-0 border-t border-tinted-grey-1 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3 sm:flex sm:justify-end sm:border-t-0 sm:px-8 sm:pb-7 sm:pt-2",
              "shrink-0 border-t border-v3-border px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3 sm:flex sm:justify-end sm:px-6 sm:pb-5 sm:pt-4",
            )}
          >
            <DialogPrimitive.Close asChild>
              <button
                className={pick(
                  "flex min-h-12 w-full items-center justify-center rounded-xl bg-primary px-8 text-base font-bold text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:min-h-11 sm:w-auto",
                  "flex min-h-12 w-full items-center justify-center rounded-full bg-v3-ink px-6 font-v3 text-[15px] font-semibold text-v3-on-ink transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand sm:min-h-9 sm:w-auto sm:text-[13.5px]",
                )}
                data-testid="whats-new-got-it"
                type="button"
              >
                {t("whatsNew.gotIt")}
              </button>
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
